import { FC, MutableRefObject, ReactNode, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ExitSlipPrintModal from './ExitSlipPrintModal';
import ExitSlipLessonRow, { ExitSlipRowVariant } from './ExitSlipLessonRow';
import { LessonItem } from './ExitSlipModalAssignBodyQuery';
import { LessonDate } from './ExitSlipModalContainer';
import { ExitSlipModalType, selectedExitSlipModal } from '../../redux/reducers/exitSlipModalSlice';
import { useAppSelector } from '../../redux/hooks';
import { selectedExitSlipFilter } from '../../redux/reducers/filterExitSlipSlice';
import ExitSlipCancelConfirm, { ExitSlipCancelTodayConfirm, exitSlipConfirmLine } from './ExitSlipCancelConfirm';
import { Toast } from 'primereact/toast';
import { fetchQuery, useMutation, useRelayEnvironment } from 'react-relay';
import { format, isToday } from 'date-fns';
import { cancelClassExitSlipById, getClassExitSlipsByFilter } from '../../graphQLOperations/ExitSlip';
import { ExitSlipCancelClassExitSlipMutation } from '../../graphQLOperations/__generated__/ExitSlipCancelClassExitSlipMutation.graphql';
import { ExitSlipClassExitSlipsQuery } from '../../graphQLOperations/__generated__/ExitSlipClassExitSlipsQuery.graphql';
import {
  applyExitSlipReassignment,
  describeExitSlipCancel,
  ExitSlipPastTag,
  getBlockedDays,
} from '../../businessLogicService/exitSlipSnapshotHelper';

interface Props {
  variant: ExitSlipRowVariant;
  lessonDates: LessonDate[];
  setLessonDates: (a: LessonDate[]) => void;
  lessons: LessonItem[];
  setSaveButtonDisabled: (s: boolean) => void;
  //days locked by tickets this panel cannot edit
  savedClaims: ExitSlipPastTag[];
  //days held by the panel's own rows, including unsaved picks
  stagedClaims: ExitSlipPastTag[];
  pastTags: ExitSlipPastTag[];
  //today's ticket renders through this list too, so it keeps the shared edit/cancel/print wiring
  highlighted?: boolean;
  emptyState?: ReactNode;
  toast?: MutableRefObject<Toast | null>;
  setApplyRefetchSnapshot?: (s: boolean) => void;
  refreshPanel?: () => void;
}

export interface PrintModalData {
  date: string;
  title: string;
  activityId: string | undefined;
  visible: boolean;
  fullScreenButton: boolean;
}

export interface CancelClassExitSlipModalData {
  classExitSlipId: string;
  title: string;
  today: boolean;
  responseCount: number;
}

const ROWS_PER_PAGE = 5;

const pagerButtonClass =
  'tw-flex tw-size-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white hover:tw-bg-black/[0.04] disabled:tw-cursor-default disabled:tw-opacity-40';

const ExitSlipModalBodyData: FC<Props> = ({
  variant,
  lessonDates,
  setLessonDates,
  lessons,
  setSaveButtonDisabled,
  savedClaims,
  stagedClaims,
  pastTags,
  highlighted = false,
  emptyState,
  toast,
  setApplyRefetchSnapshot,
  refreshPanel,
}) => {
  const { type } = useAppSelector(selectedExitSlipModal);
  const exitSlipFilterRedux = useAppSelector(selectedExitSlipFilter);
  const environment = useRelayEnvironment();
  const [cancelClassExitSlip] = useMutation<ExitSlipCancelClassExitSlipMutation>(cancelClassExitSlipById);

  const [page, setPage] = useState(0);
  const [cancelClassExitSlipModal, setCancelClassExitSlipModal] = useState<CancelClassExitSlipModalData | null>(null);
  const [printModalData, setPrintModalData] = useState<PrintModalData>({
    date: '',
    title: '',
    activityId: undefined,
    visible: false,
    fullScreenButton: false,
  });

  const pageCount = Math.max(1, Math.ceil(lessons.length / ROWS_PER_PAGE));

  useEffect(() => {
    //a save can shrink the list out from under the current page
    if (page > pageCount - 1) setPage(0);
  }, [pageCount, page]);

  useEffect(() => {
    //TODO change this assignment to the parent??
    if (lessons.length && type === ExitSlipModalType.ASSIGN) {
      setLessonDates(
        lessons.map(el => ({
          curriculumLessonId: el.id,
          date: null,
          classExitSlipId: '',
          isReassinged: false,
          isEdited: false,
        })),
      );
    }
  }, [lessons]);

  //rows are keyed by lesson, not by position: the Today section lifts a row out of this list
  //and positional bookkeeping would silently write the edit to the wrong lesson. The ticket id
  //breaks the remaining tie — one lesson can hold several rows for a class once a canceled
  //ticket stays on file beside a later one.
  const indexOf = (row: LessonItem) =>
    lessonDates.findIndex(
      el => el.curriculumLessonId === row.id && (!row.classExitSlip || el.classExitSlipId === row.classExitSlip),
    );

  const dateFor = (row: LessonItem) => lessonDates[indexOf(row)]?.date ?? null;

  const showError = () =>
    toast?.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: 'Something went wrong. Please try again',
    });

  const countTodaysResponses = (classExitSlipId: string) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return fetchQuery<ExitSlipClassExitSlipsQuery>(environment, getClassExitSlipsByFilter, {
      filter: { dueOnFrom: today, dueOnTo: today, classIds: exitSlipFilterRedux.classes },
      learnosityExitSlipSessionsFilter: { sessionStartedOn: today },
    })
      .toPromise()
      .then(
        data => data?.classExitSlips.find(slip => slip.id === classExitSlipId)?.learnosityExitSlipSessions?.length ?? 0,
      );
  };

  const openCancel = (row: LessonItem, date: Date | null) => {
    const classExitSlipId = row.classExitSlip as string;
    const open = (today: boolean, responseCount: number) =>
      setCancelClassExitSlipModal({ title: row.title, classExitSlipId, today, responseCount });

    if (!date || !isToday(date)) {
      open(false, 0);
      return;
    }

    countTodaysResponses(classExitSlipId)
      .then(count => open(true, count))
      .catch(showError);
  };

  const handleCancelConfirm = () => {
    if (!cancelClassExitSlipModal) return;
    const { classExitSlipId, title, responseCount } = cancelClassExitSlipModal;
    setCancelClassExitSlipModal(null);

    cancelClassExitSlip({
      variables: { input: { classExitSlipId } },
      onCompleted: response => {
        if (response.cancelClassExitSlip.errors?.length) {
          showError();
          return;
        }
        const { header, detail } = describeExitSlipCancel(title, responseCount);
        toast?.current?.show({ severity: 'success', summary: header, detail });
        refreshPanel?.();
        //refetch key to trigger exit slip snapshot
        setApplyRefetchSnapshot?.(true);
      },
      onError: showError,
    });
  };

  const handleDateChange = (row: LessonItem, day: Date) => {
    const index = indexOf(row);
    if (index < 0) return;

    const newDates = [...lessonDates];
    const current = newDates[index];

    newDates[index] = {
      ...current,
      date: day,
      curriculumLessonId: row.id,
      isEdited: true,
      isReassinged:
        type === ExitSlipModalType.EDIT && applyExitSlipReassignment(current.date, day) ? true : current.isReassinged,
    };

    setLessonDates(newDates);
    setSaveButtonDisabled(false);
  };

  if (!lessons.length) return <>{emptyState ?? null}</>;

  const visible = lessons.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-3">
        {visible.map(el => {
          const date = dateFor(el);

          return (
            <ExitSlipLessonRow
              key={el.classExitSlip ?? el.id}
              variant={el.canceledDate ? ExitSlipRowVariant.CANCELED : variant}
              unit={el.unit}
              title={el.title}
              date={date}
              blocked={getBlockedDays(savedClaims, stagedClaims, date)}
              pastTags={pastTags}
              canceledDate={el.canceledDate}
              highlighted={highlighted && !el.canceledDate}
              printDisabled={!el.activityId}
              onDateChange={day => handleDateChange(el, day)}
              onCancel={() => openCancel(el, date)}
              onPrint={() =>
                setPrintModalData({
                  title: el.title,
                  date: date?.toISOString() ?? '',
                  activityId: el.activityId,
                  visible: true,
                  fullScreenButton: false,
                })
              }
            />
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="tw-mt-6 tw-flex tw-items-center tw-justify-end tw-gap-3">
          <button
            type="button"
            aria-label="Previous page"
            disabled={page === 0}
            onClick={() => setPage(prev => prev - 1)}
            className={pagerButtonClass}
          >
            <ChevronLeft size={20} strokeWidth={2} className="tw-text-neutral-950" />
          </button>
          <span className="tw-font-atkinson tw-text-base tw-leading-5 tw-text-neutral-950">
            <span className="tw-font-bold">{page + 1}</span>/{pageCount}
          </span>
          <button
            type="button"
            aria-label="Next page"
            disabled={page >= pageCount - 1}
            onClick={() => setPage(prev => prev + 1)}
            className={pagerButtonClass}
          >
            <ChevronRight size={20} strokeWidth={2} className="tw-text-neutral-950" />
          </button>
        </div>
      )}

      {cancelClassExitSlipModal &&
        toast &&
        setApplyRefetchSnapshot &&
        (cancelClassExitSlipModal.today ? (
          <ExitSlipCancelTodayConfirm
            responseCount={cancelClassExitSlipModal.responseCount}
            onKeep={() => setCancelClassExitSlipModal(null)}
            onConfirm={handleCancelConfirm}
          />
        ) : (
          <ExitSlipCancelConfirm
            title="Cancel future Exit Ticket?"
            body={exitSlipConfirmLine(
              'Students will no longer be able to take it on this date but you can always reassign it.',
            )}
            keepLabel="Keep ticket"
            confirmLabel="Yes, cancel ticket"
            onKeep={() => setCancelClassExitSlipModal(null)}
            onConfirm={handleCancelConfirm}
          />
        ))}
      {printModalData.visible && (
        <ExitSlipPrintModal setPrintModalData={setPrintModalData} printModalData={printModalData} />
      )}
    </>
  );
};

export default ExitSlipModalBodyData;
